import { z } from 'zod';

// ───────────────────────────── Zapotrzebowania i rezerwacje (docs/14 §2.5, §2.7) ─────────────────────────────

export const DEMAND_SOURCES = { umowa: 'Z umowy', ekipa: 'Od ekipy', reczne: 'Ręczne' } as const;
export type DemandSource = keyof typeof DEMAND_SOURCES;

export const DEMAND_STATUSES = { otwarte: 'Otwarte', zrealizowane: 'Zrealizowane', anulowane: 'Anulowane' } as const;
export type DemandStatus = keyof typeof DEMAND_STATUSES;

/** Gotowość materiałowa zadania / sprawy (etap 4 doda „czeka na dostawę na czas”). */
export const READINESS = {
  zabezpieczone: 'Zabezpieczone',
  dostawa: 'Czeka na dostawę na czas',
  zagrozone: 'Zagrożone',
  brak: 'Brak do zamówienia',
  do_dopasowania: 'Pozycje do dopasowania',
} as const;
export type Readiness = keyof typeof READINESS;

/** Brak „zagrażający” — gdy do terminu gotowości zostało tyle dni roboczych lub mniej. */
export const RISK_WORKDAYS = 3;

export const NEEDED_BY_SOURCES = { zadanie: 'z terminu zadania', zakres: 'z planowanego terminu realizacji', reczny: 'ustawiony ręcznie' } as const;
export type NeededBySource = keyof typeof NEEDED_BY_SOURCES;

const str = (max: number) => z.string().trim().max(max);
const qty = z.number().positive('Ilość musi być większa od zera').max(1_000_000);

/** Pozycja zgłoszenia ekipy: produkt z katalogu albo opis (dopasuje magazynier). */
export const crewDemandLineSchema = z
  .object({
    /** istniejąca pozycja (zmiana zgłoszenia) */
    id: z.string().uuid().nullable().default(null),
    itemId: z.string().uuid().nullable().default(null),
    description: str(300).default(''),
    unit: str(10).default('szt.'),
    quantity: qty,
    /** świadomie ponad potrzebę z umowy */
    additional: z.boolean().default(false),
  })
  .refine((l) => !!l.itemId || l.description.length >= 2, 'Wybierz produkt albo opisz pozycję');
export type CrewDemandLineInput = z.infer<typeof crewDemandLineSchema>;

export const crewDemandSchema = z.object({
  taskId: z.string().uuid(),
  lines: z.array(crewDemandLineSchema).min(1, 'Dodaj co najmniej jedną pozycję').max(100),
  comment: str(2000).default(''),
  photoIds: z.array(z.string().uuid()).max(10).default([]),
  /** identyfikator nadany na telefonie / w formularzu — powtórka nie tworzy drugiego zgłoszenia */
  requestKey: str(80).min(8).nullable().default(null),
});
export type CrewDemandInput = z.infer<typeof crewDemandSchema>;

/** Zmiana zgłoszenia (pełna lista pozycji; pominięta pozycja = anulowana). */
export const crewDemandUpdateSchema = z.object({
  lines: z.array(crewDemandLineSchema).max(100),
  comment: str(2000).default(''),
  photoIds: z.array(z.string().uuid()).max(10).default([]),
});
export type CrewDemandUpdateInput = z.infer<typeof crewDemandUpdateSchema>;

export const demandLineChangeSchema = z.object({
  /** nowa ilość potrzebna (mniej = anulowanie części; nie mniej niż wydano) */
  quantity: z.number().min(0).max(1_000_000),
  reason: str(500).min(3, 'Podaj przyczynę zmiany'),
});

export const demandLineMatchSchema = z.object({ itemId: z.string().uuid(), quantity: qty.optional() });

export const demandNeededBySchema = z.object({ neededBy: z.string().regex(/^\d{4}-\d{2}-\d{2}$/).nullable() });

export const scopePlannedDateSchema = z.object({ plannedDate: z.string().regex(/^\d{4}-\d{2}-\d{2}$/).nullable() });

export const manualReservationSchema = z.object({
  warehouseId: z.string().uuid(),
  itemId: z.string().uuid(),
  quantity: qty,
  /** pozycja zapotrzebowania albo cel opisowy */
  demandLineId: z.string().uuid().nullable().default(null),
  purpose: str(300).default(''),
  expiresAt: z.string().regex(/^\d{4}-\d{2}-\d{2}$/).nullable().default(null),
});
export type ManualReservationInput = z.infer<typeof manualReservationSchema>;

export const reservationReleaseSchema = z.object({ quantity: qty, reason: str(500).min(3, 'Podaj przyczynę') });

export const reservationTransferSchema = z.object({
  toDemandLineId: z.string().uuid(),
  quantity: qty,
  reason: str(500).min(3, 'Podaj przyczynę przeniesienia'),
});
export type ReservationTransferInput = z.infer<typeof reservationTransferSchema>;

export const demandQuerySchema = z.object({
  warehouseId: z.string().uuid().optional(),
  source: z.enum(['umowa', 'ekipa', 'reczne']).optional(),
  trade: str(4).optional(),
  /** braki / nowe i zmienione zgłoszenia ekip / pozycje opisowe / wszystkie otwarte */
  view: z.enum(['otwarte', 'braki', 'zmienione', 'do_dopasowania', 'wszystkie']).default('otwarte'),
  caseId: z.string().uuid().optional(),
  taskId: z.string().uuid().optional(),
  q: str(100).optional(),
});
export type DemandQuery = z.infer<typeof demandQuerySchema>;

export interface DemandLineDto {
  id: string;
  demandId: string;
  itemId: string | null;
  code: string;
  name: string;
  unit: string;
  description: string;
  quantity: number;
  cancelled: number;
  issued: number;
  reserved: number;
  /** w tym skompletowane (etap 3) */
  picked: number;
  /** zamówione u dostawców i jeszcze nieprzyjęte (przydział pozycji zamówienia) */
  ordered: number;
  /** najpóźniejszy oczekiwany termin dostawy pokrycia; null — brak albo bez terminu */
  orderedBy: string | null;
  /** pokrycie z zamówienia jeszcze niezłożonego u dostawcy */
  orderedUnplaced: boolean;
  /** brak = potrzebne − anulowane − wydane − zarezerwowane − zamówione */
  shortage: number;
  additional: boolean;
  fromContract: boolean;
  matchedAt: string | null;
  /** zmiana od ekipy po rozpoczęciu kompletacji — czeka na decyzję magazyniera */
  pendingQuantity: number | null;
}

export interface DemandDto {
  id: string;
  source: DemandSource;
  status: DemandStatus;
  companyId: string;
  companyCode: string;
  warehouseId: string;
  warehouseCode: string;
  caseId: string | null;
  caseNumber: string | null;
  caseTitle: string | null;
  contractId: string | null;
  contractNumber: string | null;
  scopeId: string | null;
  tradeCode: string | null;
  taskId: string | null;
  taskNumber: string | null;
  taskDateFrom: string | null;
  teamName: string | null;
  neededBy: string | null;
  neededBySource: NeededBySource | null;
  comment: string;
  photoIds: string[];
  requestedByName: string | null;
  createdAt: string;
  changedAt: string | null;
  /** zgłoszenie ekipy nowe lub zmienione, a magazynier go jeszcze nie przejrzał */
  unreviewed: boolean;
  readiness: Readiness | null;
  lines: DemandLineDto[];
}

export interface DemandEventDto {
  id: number;
  at: string;
  kind: string;
  lineName: string | null;
  data: Record<string, unknown>;
  reason: string;
  userName: string | null;
}

export interface ShortageByItemDto {
  itemId: string;
  code: string;
  name: string;
  unit: string;
  warehouseId: string;
  warehouseCode: string;
  onHand: number;
  available: number;
  shortage: number;
  earliestNeededBy: string | null;
  lines: { demandId: string; lineId: string; caseNumber: string | null; taskNumber: string | null; neededBy: string | null; shortage: number; source: DemandSource }[];
}

export interface ReservationDto {
  id: string;
  warehouseId: string;
  warehouseCode: string;
  itemId: string;
  itemName: string;
  unit: string;
  quantity: number;
  picked: number;
  mode: 'auto' | 'reczna';
  purpose: string;
  expiresAt: string | null;
  demandLineId: string | null;
  /** szczegóły celu — tylko gdy pracownik ma dostęp do tej sprawy (firma i branża); inaczej „inna realizacja” */
  caseId: string | null;
  caseNumber: string | null;
  taskNumber: string | null;
  neededBy: string | null;
  createdByName: string | null;
  createdAt: string;
}

/** Podgląd przeniesienia rezerwacji: kto traci zabezpieczenie (docs/14 §2.5). */
export interface ReservationTransferPreviewDto {
  from: { caseNumber: string | null; taskNumber: string | null; neededBy: string | null; reserved: number; afterShortage: number };
  to: { caseNumber: string | null; taskNumber: string | null; neededBy: string | null; shortage: number };
  quantity: number;
  /** przeniesienie zabiera towar realizacji o wcześniejszym terminie */
  earlierLoses: boolean;
}

/** Materiały sprawy / zadania (zakładka „Materiały”): potrzeby, rezerwacje, braki, wydania. */
export interface CaseMaterialsDto {
  demands: DemandDto[];
  packages: PackageDto[];
  readiness: Readiness | null;
  /** wartość materiałów wydanych na realizację (WZ − zwroty) — tylko z ceny.zakup */
  issuedValueGr: number | null;
  scopes: { id: string; tradeCode: string; companyId: string; plannedDate: string | null; contractId: string | null; contractNumber: string | null; demandState: 'brak_umowy' | 'czeka_na_wyzwalacz' | 'utworzone' | 'reczne';
    /** pozycje umowy (z aneksami) bez powiązania z katalogiem — nie tworzą potrzeb materiałowych; do sprawdzenia */
    unlinkedItems: string[];
  }[];
  canSync: boolean;
  canEditPlannedDate: boolean;
}

/** Zapotrzebowanie widziane przez ekipę (bez cen i danych innych spraw). */
export interface CrewDemandViewDto {
  taskId: string;
  /** potrzeby z umowy dla zakresów zadania — żeby nie zgłaszać drugi raz */
  contractNeeds: { itemId: string; name: string; unit: string; quantity: number; reserved: number; issued: number }[];
  requests: DemandDto[];
}

// ───────────────────────────── Pakiety materiałowe (docs/14 §2.8) ─────────────────────────────

export const PACKAGE_STATUSES = {
  do_przygotowania: 'Do przygotowania',
  w_kompletacji: 'W kompletacji',
  czesciowo_gotowy: 'Częściowo gotowy',
  gotowy: 'Gotowy do odbioru',
  czesciowo_wydany: 'Częściowo wydany',
  wydany: 'Wydany',
  anulowany: 'Anulowany',
} as const;
export type PackageStatus = keyof typeof PACKAGE_STATUSES;

export const packageCreateSchema = z.object({
  taskId: z.string().uuid(),
  /** pozycje zapotrzebowań i ilości; brak = wszystko, co jeszcze nie jest w pakietach */
  lines: z.array(z.object({ demandLineId: z.string().uuid(), quantity: z.number().positive().max(1_000_000) })).max(200).optional(),
  plannedPickup: z.string().regex(/^\d{4}-\d{2}-\d{2}$/).nullable().default(null),
  pickupPlace: str(500).default(''),
  note: str(1000).default(''),
});
export type PackageCreateInput = z.infer<typeof packageCreateSchema>;

export const packageUpdateSchema = z.object({
  plannedPickup: z.string().regex(/^\d{4}-\d{2}-\d{2}$/).nullable(),
  pickupPlace: str(500).default(''),
  note: str(1000).default(''),
  recipientUserId: z.string().uuid().nullable().default(null),
});

/** Kompletacja: ustawienie ilości skompletowanej pozycji (więcej — kompletacja, mniej — rozkompletowanie). */
export const packagePickSchema = z.object({
  lines: z.array(z.object({ lineId: z.string().uuid(), picked: z.number().min(0).max(1_000_000), substituteNote: str(300).optional() })).min(1).max(200),
  reason: str(300).default(''),
});

export const packageIssueSchema = z.object({
  /** odbierający (członek ekipy) — wymagany */
  recipientUserId: z.string().uuid(),
  /** ilości do wydania; brak = wszystko, co skompletowane */
  lines: z.array(z.object({ lineId: z.string().uuid(), quantity: z.number().min(0).max(1_000_000) })).max(200).optional(),
  requestKey: str(80).min(8).nullable().default(null),
});

export const packageCancelSchema = z.object({ reason: str(500).min(3, 'Podaj przyczynę') });

export const pendingDecisionSchema = z.object({ accept: z.boolean(), reason: str(500).default('') });

export interface PackageLineDto {
  id: string;
  demandLineId: string;
  itemId: string;
  code: string;
  name: string;
  unit: string;
  required: number;
  picked: number;
  issued: number;
  /** zarezerwowane dla pozycji zapotrzebowania (wspólnie dla wszystkich jej pakietów) */
  reserved: number;
  /** czego nie da się skompletować z rezerwacji (brak towaru) */
  missing: number;
  substituteNote: string;
  /** potrzeba zmalała poniżej skompletowanego — do rozkompletowania */
  toUnpick: number;
  /** zmiana od ekipy czeka na decyzję */
  pendingQuantity: number | null;
}

export interface PackageDto {
  id: string;
  number: string;
  status: PackageStatus;
  companyId: string;
  companyCode: string;
  warehouseId: string;
  warehouseCode: string;
  taskId: string | null;
  taskNumber: string | null;
  taskDateFrom: string | null;
  caseId: string | null;
  caseNumber: string | null;
  teamId: string | null;
  teamName: string | null;
  recipientUserId: string | null;
  recipientName: string | null;
  plannedPickup: string | null;
  pickupPlace: string;
  note: string;
  readyAt: string | null;
  createdAt: string;
  lines: PackageLineDto[];
  issues: { documentId: string; number: string | null; at: string; issuedByName: string | null; recipientName: string | null; lines: { name: string; quantity: number; unit: string }[] }[];
}

/** Zadania z potrzebami, które nie są jeszcze w pakietach (do „Nowy pakiet”). */
export interface PackageCandidateDto {
  taskId: string;
  taskNumber: string;
  taskTitle: string;
  dateFrom: string | null;
  teamName: string | null;
  caseNumber: string | null;
  warehouseId: string;
  companyCode: string;
  lines: { demandLineId: string; name: string; unit: string; open: number; reserved: number }[];
}

/** Sprawdzenie materiałów przy planowaniu zadania (docs/14 §2.7): gotowość na proponowany termin i pozycje z problemem — bez cen. */
export interface TaskMaterialsCheckDto {
  readiness: Readiness | null;
  /** termin gotowości wynikający z proponowanego początku zadania */
  neededBy: string | null;
  problems: { name: string; unit: string; shortage: number; ordered: number; orderedBy: string | null; unplaced: boolean; unmatched: boolean; caseNumber: string | null }[];
}

/** Gotowość, przy której planowanie wymaga świadomego „Zaplanuj mimo braków”. */
export const READINESS_WARN: Readiness[] = ['brak', 'zagrozone', 'do_dopasowania'];
